import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import HistoryIcon from "@mui/icons-material/History";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { alpha, styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import { type MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import ProposalIssues, { ProposalIssueLevel, type ProposalIssue } from "work/proposals/issues/proposal-issues";
import {
  proposalUnreadChangeFields,
  type ProposalUnreadEnvelope,
} from "work/proposals/negotiation/proposal-field-unread";
import ProposalField, { ProposalFieldCategoryType } from "work/proposals/proposal-fields/proposal-field";

const TabLabelContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  boxSizing: "border-box",
  flexWrap: "nowrap",
  gap: "6px",
  marginLeft: theme.spacing(-2),
  paddingLeft: theme.spacing(2),
  whiteSpace: "nowrap",
  "&:hover .status-chip-label-text": {
    marginLeft: theme.spacing(0.375),
    // Wide enough for the longest label, "unsubmitted changes" (#5812); 15 cut it to "unsubmitted chang".
    maxWidth: theme.spacing(20),
    opacity: 1,
  },
}));

const BadgeContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "0px",
  flexDirection: "row",
  flexWrap: "nowrap",
  "& > .MuiChip-root": {
    border: 0,
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  height: theme.spacing(2.5),
  fontSize: "0.65rem",
  fontWeight: 600,
  overflow: "hidden",
  transition: theme.transitions.create("max-width", {
    duration: theme.transitions.duration.shortest,
  }),
  "& .MuiChip-icon": {
    fontSize: "0.85rem",
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(-0.25),
  },
  "& .MuiChip-label": {
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
  },
}));

const StatusChipLabel = styled("span")(() => ({
  alignItems: "center",
  display: "inline-flex",
  whiteSpace: "nowrap",
}));

const StatusChipLabelText = styled("span")(({ theme }) => ({
  display: "inline-block",
  marginLeft: 0,
  maxWidth: 0,
  opacity: 0,
  overflow: "hidden",
  transition: theme.transitions.create(["margin-left", "max-width", "opacity"], {
    duration: theme.transitions.duration.shortest,
  }),
}));

const PopoverContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  maxWidth: theme.spacing(42),
  padding: theme.spacing(1.25),
}));

const PopoverSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
}));

const PopoverMessage = styled(Typography)(() => ({
  fontSize: "0.75rem",
  lineHeight: 1.35,
}));

/**
 * A popover that only reports lets every pointer event through to the page under it. One whose issues
 * can be pressed takes them, and stays open while the pointer is over it (#6097).
 */
const StatusPopper = styled(Popper, {
  shouldForwardProp: (prop) => prop !== "interactive",
})<{ interactive: boolean }>(({ theme, interactive }) => ({
  pointerEvents: interactive ? "auto" : "none",
  zIndex: theme.zIndex.tooltip,
}));

const PopperSurface = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "interactive",
})<{ interactive: boolean }>(({ theme, interactive }) => ({
  backdropFilter: "blur(10px) brightness(105%)",
  backgroundColor: alpha(theme.palette.background.paper, 0.5),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  pointerEvents: interactive ? "auto" : "none",
}));

/** One issue in the popover, which takes the reader to it — as the same issue in the tab's list does. */
const PopoverIssueButton = styled(ButtonBase)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  display: "block",
  fontFamily: theme.typography.fontFamily,
  fontSize: "0.75rem",
  lineHeight: 1.35,
  margin: theme.spacing(0, -0.5),
  padding: theme.spacing(0.25, 0.5),
  textAlign: "left",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    textDecoration: "underline",
  },
  "&.Mui-focusVisible": {
    outline: `2px solid ${theme.palette.primary.main}`,
  },
}));

/**
 * What a proposal tab is advertising: its blocking issues, its warnings, the counterparty changes the
 * viewer has not looked at, the changes the viewer has not saved, the saved changes the viewer has
 * not submitted, and the counterparty's changes the viewer's side has resolved this round (#7086).
 *
 * Comments are deliberately absent. They used to be counted here per category, back when a proposal
 * carried a comment thread per field; it now has one conversation for the whole negotiation, so an
 * unread comment belongs to no tab in particular and the same count on all six tabs would only claim a
 * division that no longer exists. The Comments pane and its button carry that signal.
 */
type ProposalIssuesBadgeProps = {
  issues?: ProposalIssues;
  category: ProposalFieldCategoryType;
  title?: string;
  /** The viewer's own unsaved changes on this tab. */
  pendingChangeCount?: number;
  /**
   * What those unsaved changes are waiting for — "not yet saved", "not yet shared with reviewers". The
   * page decides, because it depends on who a save reaches and not on the tab (#5812).
   */
  pendingChangeStatus?: string;
  /** The viewer's own saved changes on this tab that the counterparty cannot see until a submission. */
  unsubmittedChangeCount?: number;
  /** What those saved changes are waiting for — "not yet submitted to the vendor". */
  unsubmittedChangeStatus?: string;
  /** The counterparty's incoming changes on this tab that the viewer's side has accepted, kept or countered. */
  resolvedChangeCount?: number;
  /**
   * The negotiation collaboration envelope, used to derive this tab's unviewed-change count the same way
   * `commentThreads`/`issues` are used to derive comment/issue counts — no separate per-tab plumbing.
   * Omitted (e.g. before the negotiation stream exists) simply shows no unviewed-change chip.
   */
  envelope?: ProposalUnreadEnvelope;
  /** Required alongside `envelope` — the unviewed-change count never counts the viewer's own edits. */
  currentUserId?: string;
  /**
   * Narrows the category to the fields one part of a tab holds — the Policies tab's Client and Vendor
   * collections each badge only their own (#6035). Pass a function declared at module scope: it keys
   * the badge's summary, so a fresh one each render would recompute it every time.
   */
  fieldFilter?: (field: ProposalField) => boolean;
  /**
   * Takes the reader to an issue pressed in the popover: the same answer as a press on that issue in
   * the tab's own list (#6097). Omitted, the popover's issues are text.
   */
  onIssueSelected?: (issue: ProposalIssue) => void;
};

export default function ProposalIssuesBadge(props: Readonly<ProposalIssuesBadgeProps>) {
  const { issues, category, title, envelope, currentUserId, fieldFilter, onIssueSelected } = props;
  const pendingChangeCount = props.pendingChangeCount ?? 0;
  const pendingChangeStatus = props.pendingChangeStatus ?? "not yet saved";
  const unsubmittedChangeCount = props.unsubmittedChangeCount ?? 0;
  const unsubmittedChangeStatus = props.unsubmittedChangeStatus ?? "not yet submitted";
  const resolvedChangeCount = props.resolvedChangeCount ?? 0;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closePopoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const badgeSummary = useMemo(() => {
    const counts = (field: ProposalField) => field.category === category && (!fieldFilter || fieldFilter(field));
    const filteredIssues = issues?.entries.filter((issue) => counts(issue.field)) ?? [];
    const criticalIssues = filteredIssues.filter((issue) => issue.level === ProposalIssueLevel.Critical);
    const warningIssues = filteredIssues.filter((issue) => issue.level === ProposalIssueLevel.Warning);
    const unviewedChangeFields = envelope ? proposalUnreadChangeFields(envelope, currentUserId) : [];
    const numUnviewedChanges = unviewedChangeFields.filter(counts).length;

    return {
      criticalIssues,
      warningIssues,
      numErrors: criticalIssues.length,
      numWarnings: warningIssues.length,
      numUnviewedChanges,
    };
  }, [category, issues, envelope, currentUserId, fieldFilter]);

  function selectIssue(event: MouseEvent<HTMLElement>, issue: ProposalIssue) {
    // The popover is drawn in a portal, but React still bubbles the press to the tab it belongs to,
    // and the tab's own change would land after this one and close the editor the issue opened.
    event.stopPropagation();
    clearClosePopoverTimeout();
    setAnchorEl(null);
    onIssueSelected?.(issue);
  }

  function renderIssue(issue: ProposalIssue, index: number) {
    // One field can carry more than one issue, so the field alone is not a unique key.
    const key = `${issue.field.cacheKey}-${index}`;
    if (!onIssueSelected) return <PopoverMessage key={key}>{issue.description}</PopoverMessage>;
    return (
      <PopoverIssueButton key={key} onClick={(event) => selectIssue(event, issue)}>
        {issue.description}
      </PopoverIssueButton>
    );
  }

  function renderPopoverContent() {
    if (
      badgeSummary.criticalIssues.length === 0 &&
      badgeSummary.warningIssues.length === 0 &&
      badgeSummary.numUnviewedChanges === 0 &&
      pendingChangeCount === 0 &&
      unsubmittedChangeCount === 0 &&
      resolvedChangeCount === 0
    ) {
      return "";
    }

    return (
      <PopoverContent>
        {badgeSummary.criticalIssues.length > 0 && (
          <PopoverSection>
            <Typography variant="subtitle2">Blockers</Typography>
            {badgeSummary.criticalIssues.map((issue, index) => renderIssue(issue, index))}
          </PopoverSection>
        )}
        {badgeSummary.warningIssues.length > 0 && (
          <PopoverSection>
            <Typography variant="subtitle2">Warnings</Typography>
            {badgeSummary.warningIssues.map((issue, index) => renderIssue(issue, index))}
          </PopoverSection>
        )}
        {pendingChangeCount > 0 && (
          <PopoverSection>
            <Typography variant="subtitle2">Unsaved</Typography>
            <PopoverMessage key={`pending-change-${category}`}>
              {getStatusLabel(pendingChangeCount, "change")} {pendingChangeStatus}.
            </PopoverMessage>
          </PopoverSection>
        )}
        {unsubmittedChangeCount > 0 && (
          <PopoverSection>
            <Typography variant="subtitle2">Unsubmitted</Typography>
            <PopoverMessage key={`unsubmitted-change-${category}`}>
              {getStatusLabel(unsubmittedChangeCount, "saved change")} {unsubmittedChangeStatus}.
            </PopoverMessage>
          </PopoverSection>
        )}
        {resolvedChangeCount > 0 && (
          <PopoverSection>
            <Typography variant="subtitle2">Resolved</Typography>
            <PopoverMessage key={`resolved-change-${category}`}>
              {getStatusLabel(resolvedChangeCount, "incoming change")} resolved by your side since the last
              submission.
            </PopoverMessage>
          </PopoverSection>
        )}
        {badgeSummary.numUnviewedChanges > 0 && (
          <PopoverSection>
            <Typography variant="subtitle2">Changes</Typography>
            <PopoverMessage key={`unviewed-change-${category}`}>
              {badgeSummary.numUnviewedChanges} unviewed change{badgeSummary.numUnviewedChanges > 1 ? "s" : ""}.
            </PopoverMessage>
          </PopoverSection>
        )}
      </PopoverContent>
    );
  }
  const popoverContent = renderPopoverContent();
  const isPopoverOpen = Boolean(anchorEl) && Boolean(popoverContent);
  const popperOffset = anchorEl?.closest(".Mui-selected") ? 16 : 8;

  useEffect(
    () => () => {
      if (closePopoverTimeout.current) clearTimeout(closePopoverTimeout.current);
    },
    [],
  );

  function clearClosePopoverTimeout() {
    if (!closePopoverTimeout.current) return;

    clearTimeout(closePopoverTimeout.current);
    closePopoverTimeout.current = null;
  }

  function openPopover(event: MouseEvent<HTMLElement>) {
    if (!popoverContent) return;

    clearClosePopoverTimeout();
    setAnchorEl(event.currentTarget);
  }

  function closePopover() {
    clearClosePopoverTimeout();
    closePopoverTimeout.current = setTimeout(() => {
      setAnchorEl(null);
    }, 100);
  }

  return (
    <>
      <TabLabelContainer
        onMouseEnter={openPopover}
        onMouseLeave={closePopover}
      >
        <span>{title ?? category.toString()}</span>
        <BadgeContainer>
          {badgeSummary.numErrors > 0 && (
            <StatusChip
              aria-label={getStatusLabel(badgeSummary.numErrors, "blocker")}
              color="error"
              data-testid="issue-critical-badge"
              icon={<ErrorOutlinedIcon />}
              label={getStatusChipLabel(badgeSummary.numErrors, "blocker")}
              size="small"
              variant="outlined"
            />
          )}
          {badgeSummary.numWarnings > 0 && (
            <StatusChip
              aria-label={getStatusLabel(badgeSummary.numWarnings, "warning")}
              color="warning"
              data-testid="issue-warning-badge"
              icon={<WarningAmberIcon />}
              label={getStatusChipLabel(badgeSummary.numWarnings, "warning")}
              size="small"
              variant="outlined"
            />
          )}
          {pendingChangeCount > 0 && (
            <StatusChip
              aria-label={getStatusLabel(pendingChangeCount, "unsaved change")}
              color="info"
              data-testid="pending-change-badge"
              icon={<HistoryIcon />}
              label={getStatusChipLabel(pendingChangeCount, "unsaved change")}
              size="small"
              variant="outlined"
            />
          )}
          {unsubmittedChangeCount > 0 && (
            <StatusChip
              aria-label={getStatusLabel(unsubmittedChangeCount, "unsubmitted change")}
              color="primary"
              data-testid="unsubmitted-change-badge"
              icon={<ScheduleSendIcon />}
              label={getStatusChipLabel(unsubmittedChangeCount, "unsubmitted change")}
              size="small"
              variant="outlined"
            />
          )}
          {resolvedChangeCount > 0 && (
            <StatusChip
              aria-label={getStatusLabel(resolvedChangeCount, "resolved change")}
              color="success"
              data-testid="resolved-change-badge"
              icon={<TaskAltIcon />}
              label={getStatusChipLabel(resolvedChangeCount, "resolved change")}
              size="small"
              variant="outlined"
            />
          )}
          {badgeSummary.numUnviewedChanges > 0 && (
            // Secondary (purple), never error — an unviewed change is something to notice, not an alarm.
            <StatusChip
              aria-label={getStatusLabel(badgeSummary.numUnviewedChanges, "unviewed change")}
              color="secondary"
              data-testid="unviewed-change-badge"
              icon={<PublishedWithChangesIcon />}
              label={getStatusChipLabel(badgeSummary.numUnviewedChanges, "unviewed change")}
              size="small"
              variant="outlined"
            />
          )}
        </BadgeContainer>
      </TabLabelContainer>
      <StatusPopper
        interactive={Boolean(onIssueSelected)}
        onMouseEnter={onIssueSelected ? clearClosePopoverTimeout : undefined}
        onMouseLeave={onIssueSelected ? closePopover : undefined}
        anchorEl={anchorEl}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, popperOffset],
            },
          },
        ]}
        open={isPopoverOpen}
        placement="bottom-start"
      >
        <PopperSurface elevation={0} interactive={Boolean(onIssueSelected)}>
          {popoverContent}
        </PopperSurface>
      </StatusPopper>
    </>
  );
}

function getStatusLabel(count: number, singularLabel: string, pluralLabel = `${singularLabel}s`) {
  return `${count} ${count === 1 ? singularLabel : pluralLabel}`;
}

function getStatusChipLabel(count: number, singularLabel: string, pluralLabel = `${singularLabel}s`) {
  return (
    <StatusChipLabel>
      <span>{count}</span>
      <StatusChipLabelText className="status-chip-label-text">
        {count === 1 ? singularLabel : pluralLabel}
      </StatusChipLabelText>
    </StatusChipLabel>
  );
}
